// Record money — BETWEEN PARTIES (11 Sept 2026).
//
// Operator: a movement from one party to another where the desk earns nothing
// ("P2P, like Loki"). One party's balance moves to another party's. No desk account
// moves — no rail, no drawer, no margin — so it is not a receipt, not a
// payment and not a trade, and until now the books had no honest door for
// it: the choice was a receipt from A and a payment to B through a rail the
// money never touched, or nothing at all.
//
// One leg is a plain move (from → to). "And in return" adds a second leg
// going back (to → from), which makes it a swap between the two parties at
// whatever terms THEY agreed — the desk is only the bookkeeper.
//
// THE TWO LEGS OF A SWAP ARE THE TWO SIDES OF A PAIR, so the swap carries a
// Rate box (12 Sept 2026, operator: "screens everywhere for asset-1, asset-2
// & rate and 3-way auto calculation, that too dynamic"). Any two of forward
// amount, return amount and rate fill the third on every keystroke or paste;
// the shared calculator (lib/trade-math.ts, hooks/useTradeMath.ts) owns all
// three. The rate is a calculator aid only — the route takes two legs and no
// rate, and the posted amounts are whatever the two amount boxes show.
//
// SIGN LAW, the same one every party box keeps: positive = the party owes the
// desk, negative = the desk holds theirs. A move from A to B is a ledger send
// from A's account to B's, so A's box FALLS by the amount and B's RISES —
// exactly as Record money in lowers the payer's box. The before → after lines
// under each party (the shared BoxLines) say so before anything is posted.
//
// This file owns the form only. RecordMoney owns the mode selector and the
// page header and renders this body, the same way it renders RecordExpense.
// Posts POST /api/p2p (bff, built to the 11 Sept contract).
//
// Sweep of 14 Sept 2026 (SWEEP-SCOPE §5.7): hints gone, the return-leg block
// three columns, the cross-desk note in About, the shared Field / BoxLines /
// ErrorLine, figures cleared on success. Nothing about the posting changed.
//
// Phone pass of 19 Sept 2026 (below lg): every field already stacks full
// width through FieldRow; the return leg's amount and rate sit side by side
// as the calculator pair, its asset chips on their own line, and the fold
// toggle is a 40px tap row. lg+ renders as before. Presentation only.

import { useState, useMemo, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api, type P2pLeg } from '../lib/api';
import { useTradeMath } from '../hooks/useTradeMath';
import { tg } from '../lib/telegram';
import { ASSETS, makeReference } from '../lib/workflow';
import { amountOk, amountProblem, rateProblem } from '../lib/amount';
import { errorWords, isSentinel, nothingLanded, readWords } from '../lib/errors';
import { readState } from '../lib/read-state';
import { Combobox, type ComboboxOption } from '../components/Combobox';
import { rosterCut, usePartySearch } from '../lib/roster-cut';
import { type Desk } from '../components/DeskSwitch';
import { AssetPicker } from '../components/AssetPicker';
import { AsOfChips } from '../components/AsOfChips';
import { Field, FieldRow } from '../components/Field';
import { BoxLines } from '../components/BoxLines';
import { ErrorLine } from '../components/ErrorLine';
import { StateLine } from '../components/kit';
import { CopyRef } from '../components/CopyRef';
import { RepeatPanel, useRepeatGuard } from '../components/RepeatPanel';
import {
  partyName, partySubtitle, isStale, needsNameReview, rankForQuery, foldAsset, sameAsset, partyBoxWords,
} from '../lib/naming';
import {
  displayAsset, fmtAmount, fmtDate, fmtDay, shownAmount,
  dubaiWallToIso, backdateProblem,
} from '../lib/format';

/** A figure and its unit as ONE value (a no-break space joins them): a sentence wraps between values, never inside one. */
const money = (a: string | number | undefined, asset: string | undefined) => `${fmtAmount(a, asset)}\u00a0${displayAsset(asset)}`;

/** What the last posting said, kept apart from the live fields — the success
 *  line reads this, never whatever the boxes hold by the time it renders. */
export interface Posted {
  reference: string; asOf: string | null;
  /** The route's own legs (folded assets, the ledger's amounts) on a fresh
   *  posting; what was sent only when the answer carried none. The route's
   *  amounts are strings — fmtAmount takes either. */
  legs: Array<{ asset: string; amount: string | number }>;
  /** The books already held this reference: a retry whose first attempt
   *  landed. Nothing moved on THIS request — the first posting's amount and
   *  date stand — so the success line must not read it as a fresh move. */
  deduped: boolean;
  fromName: string; toName: string;
}

/** The answer to a move: a headline of what moved (design system §3: one line, at most ten words — a swap's two legs
 *  and a dated entry would run past it) and, under it in muted type, between whom and as of when, time of day kept.
 *  A deduped answer says so instead. */
export function PostedLine({ posted }: { posted: Posted }) {
  if (posted.deduped) {
    return (
      <StateLine tone="warn" action={<Link to="/movements" className="btn-link text-meta">Money movements →</Link>}>
        Already recorded as <span className="font-mono">{posted.reference}</span> — nothing changed; check its date.
      </StateLine>
    );
  }
  return (
    <div className="space-y-0.5">
      <StateLine tone="good" action={<CopyRef value={posted.reference} />}>
        ✓ Moved <span className="whitespace-nowrap">{money(posted.legs[0]?.amount, posted.legs[0]?.asset)}</span>
        {posted.legs[1] && <> · <span className="whitespace-nowrap">{money(posted.legs[1].amount, posted.legs[1].asset)}</span> back</>}
      </StateLine>
      <p className="text-meta text-tg-hint">{posted.fromName} → {posted.toName}{posted.asOf ? ` · as of ${fmtDate(posted.asOf)}` : ''}</p>
    </div>
  );
}

/** The assets on offer, FOLDED. A party's box speaks the fold (USDT, never
 *  USDT·TRC — the chain lives in a wallet's name and no wallet is named here),
 *  so a chip per chain would offer a distinction the entry cannot carry. The
 *  route folds whatever it is sent, so the fold is the honest vocabulary. */
const P2P_ASSETS: string[] = [...new Set(ASSETS.map((a) => foldAsset(a)))];

/** One party picker's worth of reads: the search list (with balances, so the
 *  rows carry the box words every other picker shows) and the picked row. */
function usePartyPick(desk: Desk, typed: string) {
  // Same scheme as the Party field on Record money: the first three characters name the read and are what it asks — until an answer is
  // cut, when the whole text is asked too (usePartySearch).
  const list = usePartySearch({
    base: ['parties', desk], text: typed,
    ask: (q) => api.listParties({ entity: desk, q, with_balances: true }),
    enabled: typed.length > 0,
  });
  const rows = list.data?.parties ?? [];
  // WHAT THE LIST IS WORTH — `readState` (lib/read-state.ts; Appendix B): the names, and the balances (a list the BFF flags
  // `balances_read: false` has the names and not the balances: its empty balances are not "square"). The box lines under the picker and the
  // picker's own box words take the balances' answer.
  const lookup = readState(list);
  const balancesRead = readState(list, { balances: true }) === 'known';
  // The flag itself, for the one line that says it: whatever is typed, the list on screen is one query per search.
  const flagged = lookup === 'known' && !balancesRead;
  const options: ComboboxOption[] = useMemo(() =>
    // OWN ACCOUNTS ARE NOT PARTIES HERE. A rail's party record offered in this
    // box would move a rail's balance as though it were a customer's — the
    // very thing this door exists not to do.
    rankForQuery(rows.filter((p) => p.party_type !== 'own_account'), typed).map((p) => ({
      value:     p.slug,
      label:     partyName(p),
      secondary: [partySubtitle(p, { compact: true }), balancesRead ? partyBoxWords(p) : ''].filter(Boolean).join(' · '),
      stale:     isStale(p),
      review:    needsNameReview(p.display_name),
    }))
  , [list.data, balancesRead, typed]);   // eslint-disable-line react-hooks/exhaustive-deps
  const selected = rows.find((p) => p.slug === typed && p.party_type !== 'own_account') ?? null;
  // `loading`: a list still being read (or paused) is not "no party by that name" — the picker says so while it waits.
  return { options, selected, loading: typed.length > 0 && lookup === 'waiting', error: lookup === 'failed' ? list.error : null, balancesRead, flagged, cut: rosterCut(list.data), refetch: list.refetch };
}

export function RecordBetweenParties() {
  const nav = useNavigate();
  // ONE DESK (operator, 25 Sept 2026: "cut indiaotc … we have moved every
  // asset" — 2ce2119 posted the wind-down: IndiaOTC now holds no treasury and
  // no assets, only the opening equity, the two loss lines and the interco
  // pair Dubai owes it). This screen moves a balance from one party's box to
  // another and, until today, could do it across desks — the DeskSwitch pair
  // below let either side be IndiaOTC. There is nothing left on that desk for
  // a between-parties move to touch, so the picker is gone and both sides are
  // dubaiotc, the same way NewTrade.tsx fixed its own entity. If an IndiaOTC
  // party is ever found still carrying a balance, this is a one-line revert,
  // not a rebuild — see the git history on this constant.
  const DESK: Desk = 'dubaiotc';
  // Deep link: /record?mode=p2p&from=<slug>&to=<slug>. `from` names a real
  // party on THIS desk — see PartyDetail.tsx's "Move to a party", which no
  // longer offers this door from an IndiaOTC party's page.
  const [search] = useSearchParams();
  const [from, setFrom] = useState(search.get('from') ?? '');
  const [to, setTo]     = useState(search.get('to') ?? '');
  const [asset, setAsset]   = useState<string>('AED');
  // The return leg. Closed = one-way move; open = a swap, and the leg posts.
  const [swap, setSwap]             = useState(false);
  const [backAsset, setBackAsset]   = useState<string>('USDT');
  // BOTH AMOUNTS AND THE RATE LIVE IN THE SHARED CALCULATOR (12 Sept 2026).
  // The forward leg's asset and the return leg's asset are the pair; which
  // box is quantity and which is total, and which way the rate reads, is the
  // calculator's call (lib/trade-math.ts). The hook holds the forward amount
  // even while the swap is closed, so a figure typed before "And in return"
  // opens is still there when it does. No local copy of any of the three.
  const math = useTradeMath(asset, backAsset);
  const amount     = math.amountOf(asset);
  const backAmount = math.amountOf(backAsset);
  // WHICH OF THE THREE MOVES (12 Sept 2026): the box the calculator fills says
  // "auto" on its label and goes muted, so the operator sees at a glance which
  // figures are theirs and which one followed. The hook decides, never the
  // screen. Only while the swap is open — closed, there is one box on the
  // screen and nothing visible for "auto" to point at.
  const amountAuto = swap && math.isDerived(asset);
  const backAuto   = swap && math.isDerived(backAsset);
  const rateAuto   = swap && math.rateIsDerived;
  const [notes, setNotes] = useState('');
  // Which calculator box has the cursor — its raw string shows while it does (see shownAmount).
  const [editing, setEditing] = useState<'amount' | 'back' | null>(null);

  // ── WHEN DID THIS HAPPEN? — the same field as every other posting (5 Sept
  // 2026 door). Empty means now. Dubai wall-clock, read through lib/format.ts.
  const [when, setWhen] = useState('');
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);

  const fromPick = usePartyPick(DESK, from);
  const toPick   = usePartyPick(DESK, to);
  const fromName = fromPick.selected ? partyName(fromPick.selected) : from;
  const toName   = toPick.selected   ? partyName(toPick.selected)   : to;

  // ── what was typed, checked before it is sent ────────────────────────────
  const amountN   = Number(amount) || 0;
  const backN     = Number(backAmount) || 0;
  const amountWords = amountProblem(amount);
  const backWords   = amountProblem(backAmount);
  // The rate is never posted, so a bad one blocks nothing — the calculator
  // simply leaves the third box where it was. These words say why it did not
  // move (a "3.6x" or an "abc" — commas and currency tags are cleaned up by
  // the calculator itself since 12 Sept 2026). The shared rule and sentence
  // (lib/amount.ts rateProblem, sweep 5.7, 14 Sept 2026) — one voice on every
  // rate box, not a fourth copy of the words here.
  const rateWords = rateProblem(math.rate);
  const samePartyWords = from && to && from === to
    ? 'Pick two different parties'
    : null;
  const sameAssetSwapWords = swap && sameAsset(asset, backAsset)
    ? 'The return leg is in the same asset as the first — move the difference one way instead.'
    : null;

  // ── BOTH SIDES, BEFORE AND AFTER — per asset, in desk words. The list rows
  // carry balances FOLDED (USDT), and the chips speak the fold, so the key is
  // the chip; foldAsset() is belt and braces for a value that arrived by link.
  const fromBal = fromPick.selected?.balances ?? {};
  const toBal   = toPick.selected?.balances ?? {};
  type Line = { asset: string; now: number; next: number };
  const lines = (bal: Record<string, number>, dir: 1 | -1): Line[] => {
    const a = bal[foldAsset(asset)] ?? 0;
    const out: Line[] = [{ asset, now: a, next: a - dir * amountN }];
    if (swap && !sameAssetSwapWords) {
      const b = bal[foldAsset(backAsset)] ?? 0;
      out.push({ asset: backAsset, now: b, next: b + dir * backN });
    }
    return out;
  };
  // FROM falls by the first leg and rises by the return leg; TO the reverse.
  const fromLines = lines(fromBal, 1);
  const toLines   = lines(toBal, -1);

  // ── submit ───────────────────────────────────────────────────────────────
  // THE REFERENCE IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY — minted
  // once per entry, held in a ref, cleared on success, retired when the day it
  // carries would change and the books provably hold nothing under it (the
  // same rule as Record money's refRef, and NewTrade's deal id).
  const refRef = useRef<string | null>(null);
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();
  // WHAT THE LAST POSTING SAID, kept apart from the live fields — the success
  // line reads this, never whatever the boxes hold by the time it renders.
  const [posted, setPosted] = useState<Posted | null>(null);

  const post = async (confirmRepeat: boolean) => {
    refRef.current ??= makeReference('P2P', whenIso || undefined);
    const reference = refRef.current;
    // Amounts come straight from the calculator's boxes — typed or derived,
    // what the operator sees is what posts. The rate does not travel.
    const legs: P2pLeg[] = [{ asset, amount: amountN }];
    if (swap) legs.push({ asset: backAsset, amount: backN });   // the RETURN leg, to → from
    const data = await api.p2p({
      entity:    DESK,
      from_slug: from,
      to_slug:   to,
      // `to_entity` names a SECOND desk when the two parties don't share one
      // — this screen no longer offers that, so it is always absent; the BFF
      // must not be handed a redundant field equal to `entity` either way.
      to_entity: undefined,
      legs,
      reference,
      notes:          notes.trim() || undefined,
      effective_at:   whenIso || undefined,
      confirm_repeat: confirmRepeat || undefined,
    });
    return { data, reference, asOf: whenIso || null, legs, fromName, toName };
  };
  const submit = useMutation({
    mutationFn: (opts?: { confirmRepeat?: boolean }) => guard(() => post(opts?.confirmRepeat === true)),
    onSuccess: ({ data, reference, asOf, legs, fromName, toName }) => {
      clearTwin();
      refRef.current = null;
      setPosted({ reference: data.reference ?? reference, asOf, legs: data.legs ?? legs, deduped: !!data.deduped, fromName, toName });
      // The figures leave with the entry they were typed for (§7 rule 12):
      // the date would ride into the next movement, and an amount left in
      // the box is a second tap from posting twice. The parties stay.
      setWhen(''); math.reset(); setNotes('');
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError: (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });
  const retireReference = () => {
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) refRef.current = null;
  };

  const readError = fromPick.error ? readWords(fromPick.error) : toPick.error ? readWords(toPick.error) : null;
  // A list that answered 200 but could not read its balances (balances_read: false) has no box words or lines to draw — and says so
  // once, whether or not a party is picked yet.
  const balancesUnread = fromPick.flagged || toPick.flagged;
  const retryRead = () => {
    if (fromPick.error || fromPick.flagged) void fromPick.refetch();
    if (toPick.error || toPick.flagged) void toPick.refetch();
  };
  // BOTH PARTIES PICKED FROM THE LIST, not merely typed: `selected` is the row
  // the books answered for (and never a rail), so a slug that names nobody —
  // or a name typed by hand — cannot reach the route.
  const canPost = !submit.isPending
    && !!fromPick.selected && !!toPick.selected && !samePartyWords
    && amountOk(amount)
    && (!swap || (amountOk(backAmount) && !sameAssetSwapWords))
    && !whenWords;

  return (
    <div className="space-y-3 lg:space-y-4">
      <FieldRow>
        <Field label="From">
          <Combobox
            value={from}
            onChange={setFrom}
            options={fromPick.options}
            more={fromPick.cut}
            loading={fromPick.loading}
            placeholder="Search party"
            emptyHint={from.length === 0 ? null : fromPick.error ? 'Parties could not be read.' : undefined}
          />
          {fromPick.selected && fromPick.balancesRead && fromLines.map((l) => (
            <BoxLines key={l.asset} className="mt-1" asset={l.asset} before={l.now} after={amountN > 0 ? l.next : undefined} />
          ))}
        </Field>
        <Field label="To">
          <Combobox
            value={to}
            onChange={setTo}
            options={toPick.options}
            more={toPick.cut}
            loading={toPick.loading}
            placeholder="Search party"
            emptyHint={to.length === 0 ? null : toPick.error ? 'Parties could not be read.' : undefined}
          />
          {toPick.selected && toPick.balancesRead && toLines.map((l) => (
            <BoxLines key={l.asset} className="mt-1" asset={l.asset} before={l.now} after={amountN > 0 ? l.next : undefined} />
          ))}
        </Field>
      </FieldRow>
      <ErrorLine>{samePartyWords}</ErrorLine>
      {(readError || balancesUnread) && (
        <StateLine
          tone="warn"
          more={readError ?? undefined}
          action={<button type="button" className="btn-link text-meta" onClick={retryRead}>Retry</button>}
        >
          {readError ? 'Couldn’t read parties — try again.' : 'Balances not read — try again.'}
        </StateLine>
      )}

      <FieldRow>
        <Field label="Asset">
          {/* The desk's everyday three as chips, the rest behind "more…"
              (operator, 12 Sept 2026: "Not so many, only most used 3"). The
              list is still P2P_ASSETS, folded. */}
          <AssetPicker value={asset} onChange={setAsset} options={P2P_ASSETS} />
        </Field>
        <Field label={`Amount in ${displayAsset(asset)}`} hint={amountAuto ? 'auto' : undefined}>
          <input className={`input${amountAuto ? ' text-tg-hint' : ''}`} value={shownAmount(amount, asset, amountAuto, editing === 'amount')} onChange={(e) => math.setAmount(asset, e.target.value)} onFocus={() => setEditing('amount')} onBlur={() => setEditing(null)} inputMode="decimal" />
          <ErrorLine>{amountWords}</ErrorLine>
        </Field>
      </FieldRow>

      {/* THE RETURN LEG. Closed by default: most of these are one-way. Open,
          it is a second leg going the other way, and the two together are a
          swap between the parties — at their terms, not the desk's. */}
      <div>
        <button
          type="button"
          onClick={() => setSwap((v) => !v)}
          // On the phone (19 Sept 2026, the phone-layout pass) the fold reads
          // at the 13px content size on a 40px row, so it is a tap and not a
          // poke; lg+ keeps its 12px link as before.
          className="btn-link text-meta flex items-center gap-1 py-1 max-lg:text-body max-lg:min-h-[40px]"
          aria-expanded={swap}
        >
          {/* The fold's own chevron (MoreDetails) — 21 Oct 2026 audit: a '▸' text
              glyph read as a speck beside the 12px chevron every other fold wears. */}
          {swap ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          And in return (optional)
        </button>
        {/* THE PHONE (19 Sept 2026, the phone-layout pass): the asset chips
            take the whole line, and the two calculator boxes — the amount
            back and the rate — sit side by side beneath them as the pair
            they are (any two of the three fill the third). Three across
            would give each box ~100px at 375px; one under another would
            lose the pair. At lg+ the three columns are as they were. */}
        {swap && (
          <FieldRow cols={3} className="mt-2 pl-3 border-l border-tg-secondary max-lg:grid-cols-2">
            <Field label="Asset back" className="max-lg:col-span-2">
              {/* The forward asset is not on offer here — a swap needs two
                  assets (12 Sept 2026). sameAssetSwapWords stays as belt and
                  braces for the other order: the forward asset changed to
                  match after this one was picked. */}
              <AssetPicker value={backAsset} onChange={setBackAsset} options={P2P_ASSETS} exclude={(a) => sameAsset(a, asset)} />
            </Field>
            <Field label={`Amount back in ${displayAsset(backAsset)}`} hint={backAuto ? 'auto' : undefined}>
              <input className={`input${backAuto ? ' text-tg-hint' : ''}`} value={shownAmount(backAmount, backAsset, backAuto, editing === 'back')} onChange={(e) => math.setAmount(backAsset, e.target.value)} onFocus={() => setEditing('back')} onBlur={() => setEditing(null)} inputMode="decimal" />
              <ErrorLine>{backWords}</ErrorLine>
              <ErrorLine>{sameAssetSwapWords}</ErrorLine>
            </Field>
            {/* THE RATE BETWEEN THE TWO LEGS (12 Sept 2026). Reads the way
                the desk says it — "AED per USDT", never the other way round.
                Any two of the three boxes fill the third: an amount typed over
                a rate re-derives the rate; a rate typed over two amounts moves
                the amount typed later, the first one stays (lib/trade-math.ts).
                Pasted as typed: no trimming, no coercion. */}
            <Field label="Rate" hint={sameAssetSwapWords ? undefined : `${math.rateWords}${rateAuto ? ' · auto' : ''}`}>
              <input
                className={`input${rateAuto ? ' text-tg-hint' : ''}`}
                value={math.rate}
                onChange={(e) => math.setRate(e.target.value)}
                inputMode="decimal"
                disabled={!!sameAssetSwapWords}
              />
              <ErrorLine>{rateWords}</ErrorLine>
            </Field>
          </FieldRow>
        )}
      </div>

      {/* AS OF — the chip row every entry screen carries since 14 Sept 2026
          (sweep item 23): Now is an explicit choice, the picker appears only
          when asked for, and the chips render the echo and the back-date
          words themselves. `whenIso` / `whenWords` stay this form's.
          21 Oct 2026 audit: no FieldRow around it — alone in a two-column grid
          its "optional · Dubai time" hint floated at the middle of the card. */}
      <AsOfChips
        value={when}
        // The reference carries the day — a new day, a new reference,
        // unless the old one may already be in the ledger (retireReference).
        onChange={(w) => { setWhen(w); retireReference(); }}
        disabled={submit.isPending}
      />

      {/* Notes stays open here: 4 of 4 entries typed one (§5.7). */}
      <Field label="Notes">
        <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
      </Field>

      <div className="lg:flex lg:justify-end lg:border-t lg:border-stroke-soft lg:pt-5">
        <button
          className="btn-primary w-full lg:w-auto lg:px-6"
          disabled={!canPost}
          onClick={() => submit.mutate({})}
        >
          {submit.isPending ? 'Booking…'
            : `${swap ? 'Record the swap' : 'Record the move'}${whenIso && !whenWords ? ` as of ${fmtDay(whenIso)}` : ''}`}
        </button>
      </div>

      {/* The success line names what MOVED, from what was posted. A deduped
          answer says so instead: the ledger already held this reference, and
          this request's amount and date are not what landed. */}
      {submit.isSuccess && posted && <PostedLine posted={posted} />}
      {submit.isError && !isSentinel(submit.error) && (
        <ErrorLine className="mt-1 text-body">{errorWords(submit.error)}</ErrorLine>
      )}
      {repeat && (
        <RepeatPanel
          twin={repeat}
          pending={submit.isPending || !!whenWords}
          onAlreadyBooked={() => { clearTwin(); nav(-1); }}
          // This route only ever meets repeat_suspected, so confirm_repeat is
          // the one flag it sends (the cross-shape guard is trades').
          onPostAnyway={() => submit.mutate({ confirmRepeat: true })}
        />
      )}
    </div>
  );
}
